/* eslint-disable @next/next/no-img-element */
import Button from "@/designs/Button";
import Input from "@/designs/Input";
import Loader from "@/designs/Loader";
import Modal from "@/designs/Modal";
import {
  EmployeeTimeline,
  GetEmployeesTimelineDocument,
  GetUsersDocument,
  useCreatedEmployeeTimelineDeviceMutation,
} from "@/graphql/generated/schema";
import { UserRound } from "lucide-react";
import moment from "moment";
import Link from "next/link";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { CMSModal } from "@/context";
import VerticalNavigator from "@/designs/VerticalNavigator";
import { Divider } from "antd";
import EmployeeStatus from "@/designs/ChangeEmployeeStatus";
import toast from "react-hot-toast";
interface ViewEmployeeTimelineProp {
  isOpen: boolean;
  onClose: () => void;
  timeline: EmployeeTimeline;
  allEmployees: EmployeeTimeline[] | any;
  setTimeline: (value: EmployeeTimeline) => void;
}
const ViewEmployeeTimeline = ({
  isOpen,
  onClose,
  timeline,
  allEmployees,
  setTimeline,
}: ViewEmployeeTimelineProp) => {
  const { theme } = useContext(CMSModal);

  const [signedType, setSignedType] = useState<string>(
    timeline?.signedType || "",
  );
  const [isChanged, setIsChanged] = useState(false);
  const currentIndex = allEmployees?.findIndex(
    (v: EmployeeTimeline) => v.id === timeline?.id,
  );
  const [createTimeline, { loading }] =
    useCreatedEmployeeTimelineDeviceMutation({
      refetchQueries: [GetEmployeesTimelineDocument, GetUsersDocument],
    });

  const handleMove = (direction: "up" | "down") => {
    if (!Array.isArray(allEmployees)) return;

    let newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= allEmployees.length) return; // out of bounds

    const nextVisitor = allEmployees[newIndex];
    if (nextVisitor) setTimeline(nextVisitor);
  };
  // Initialize form when modal opens or timeline changes
  useEffect(() => {
    if (timeline) {
      setSignedType(timeline.signedType || "");
      setIsChanged(false);
    }
  }, [timeline, isOpen]);

  const onStatusChange = useCallback(
    async (newStatus: string) => {
      try {
        if (newStatus === "Out") {
          await createTimeline({
            variables: {
              input: {
                signedType: "Out",
                signedOut: new Date().toISOString(),
                signedIn: timeline?.signedIn,
                employee: timeline.employee?.id,
                _id: timeline?.id,
              },
            },
          });
        } else {
          await createTimeline({
            variables: {
              input: {
                signedType: newStatus,
                signedIn: new Date().toISOString(),
                employee: timeline.employee?.id,
              },
            },
          });
        }
        onClose();

        toast.success("Status updated successfully");
      } catch {
        toast.error("Failed to update status");
      }
    },
    [createTimeline],
  );
  const start = moment(timeline?.signedIn);
  const end = moment(timeline?.signedOut);
  const diffMinutes = end.diff(start, "minutes"); // difference in minutes
  const diffSeconds = end.diff(start, "seconds"); // if you prefer seconds

  // You can format it nicely:
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {loading && <Loader />}
      <VerticalNavigator
        currentIndex={currentIndex}
        total={allEmployees.length}
        onMoveUp={() => handleMove("up")}
        onMoveDown={() => handleMove("down")}
        theme={theme}
      />

      <div className="space-y-6">
        <div className="flex items-center gap-5">
          {timeline?.employee?.img ? (
            <img
              src={timeline?.employee?.img || "/placeholder-user.jpg"}
              alt="Visitor"
              className={twMerge(
                "w-20 h-20 rounded-full border object-cover transition-colors",
                theme === "dark" ? "border-gray-800" : "border-gray-200",
              )}
            />
          ) : (
            <img
              src="/Avatar.svg"
              alt="Visitor"
              className={twMerge(
                "w-20 h-20 rounded-full border object-cover transition-colors",
                theme === "dark" ? "border-gray-800" : "border-gray-200",
              )}
            />
          )}
          <div className="flex-1 transition-colors">
            <h2
              className={twMerge(
                "text-lg font-bold transition-colors",
                theme === "dark" ? "text-gray-100" : "",
              )}
            >
              {timeline?.employee?.firstName || "Unnamed Visitor"}
            </h2>
            <p
              className={twMerge(
                "text-sm transition-colors pb-3",
                theme === "dark" ? "text-gray-400" : "text-gray-500",
              )}
            >
              {signedType && timeline.signedIn
                ? `Signed in at ${moment(timeline?.signedIn).format(
                    "h:mma, MMM Do",
                  )}`
                : `Signed out at ${moment(timeline?.signedOut).format(
                    "h:mma, MMM Do",
                  )}`}
            </p>
            <EmployeeStatus
              value={signedType || "Out"}
              onChange={(status) => onStatusChange(status)}
            />
          </div>
        </div>

        <Divider />
        <div className="grid grid-cols-2 gap-4 transition-colors">
          <h2
            className={twMerge(
              theme === "dark" ? "text-gray-400" : "text-gray-600",
            )}
          >
            Email
          </h2>
          <h2
            className={twMerge(
              "font-medium transition-colors",
              theme === "dark" ? "text-gray-100" : "",
            )}
          >
            {timeline.employee?.email}
          </h2>
          <h2
            className={twMerge(
              theme === "dark" ? "text-gray-400" : "text-gray-600",
            )}
          >
            Phone
          </h2>
          <h2
            className={twMerge(
              "font-medium transition-colors",
              theme === "dark" ? "text-gray-100" : "",
            )}
          >
            {timeline.employee?.phone || "-"}
          </h2>
          <h2
            className={twMerge(
              theme === "dark" ? "text-gray-400" : "text-gray-600",
            )}
          >
            Location
          </h2>
          <h2
            className={twMerge(
              "font-medium transition-colors",
              theme === "dark" ? "text-gray-100" : "",
            )}
          >
            {timeline?.location?.name}
          </h2>
        </div>
        <Link
          href="/employees"
          className="flex items-center gap-2 text-[#1bbae1]"
        >
          <UserRound />
          View Profile
        </Link>
        <Divider />
        <h2
          className={twMerge(
            "font-medium transition-colors",
            theme === "dark" ? "text-gray-100" : "",
          )}
        >
          In and Out
        </h2>
        <div className="grid grid-cols-2 gap-4 transition-colors">
          <h2
            className={twMerge(
              theme === "dark" ? "text-gray-400" : "text-gray-600",
            )}
          >
            Signed in
          </h2>
          <h2
            className={twMerge(
              "font-medium transition-colors",
              theme === "dark" ? "text-gray-100" : "",
            )}
          >
            {timeline.signedIn
              ? moment(timeline.signedIn).format(" h:mma, MMM Do")
              : "--"}
          </h2>
          <h2
            className={twMerge(
              theme === "dark" ? "text-gray-400" : "text-gray-600",
            )}
          >
            Signed out
          </h2>
          <h2
            className={twMerge(
              "font-medium transition-colors",
              theme === "dark" ? "text-gray-100" : "",
            )}
          >
            {timeline?.signedOut
              ? moment(timeline?.signedOut).format(" h:mma, MMM Do")
              : "--"}{" "}
          </h2>
          <h2
            className={twMerge(
              theme === "dark" ? "text-gray-400" : "text-gray-600",
            )}
          >
            Total time
          </h2>
          <h2
            className={twMerge(
              "transition-colors",
              theme === "dark" ? "text-gray-100" : "",
            )}
          >
            {timeline?.signedOut && timeline?.signedIn
              ? hours > 0
                ? `${hours}h ${minutes}m`
                : `${minutes}m (${diffSeconds}s)`
              : "--"}
          </h2>
        </div>
        {timeline.signInQue && timeline.signInQue.length > 0 && (
          <div className="space-y-4">
            <Divider />

            <h2
              className={twMerge(
                "font-medium transition-colors",
                theme === "dark" ? "text-gray-100" : "",
              )}
            >
              Sign in questions
            </h2>

            {timeline.signInQue.map((item) => (
              <div key={item?.label}>
                {/* TEXT TYPE */}
                {item?.type === "text" && (
                  <Input
                    label={item?.label || ""}
                    value={item?.answer || ""}
                    disabled
                  />
                )}

                {/* RADIO / YES-NO TYPE */}
                {item?.type === "radio" && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium ">
                      {item?.label}
                    </label>

                    <div className="flex gap-6 mt-1 transition-colors">
                      <label
                        className={twMerge(
                          "flex items-center gap-2 text-sm transition-colors",
                          theme === "dark" ? "text-gray-300" : "",
                        )}
                      >
                        <input
                          type="radio"
                          checked={item?.answer === "Yes"}
                          disabled
                          className="w-4 h-4 accent-primary"
                        />
                        Yes
                      </label>

                      <label
                        className={twMerge(
                          "flex items-center gap-2 text-sm transition-colors",
                          theme === "dark" ? "text-gray-300" : "",
                        )}
                      >
                        <input
                          type="radio"
                          checked={item?.answer === "No"}
                          disabled
                          className="w-4 h-4 accent-primary"
                        />
                        No
                      </label>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ViewEmployeeTimeline;
